import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { CornerStyle } from '@atlassian/company-hub-common';
import { CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';
import {
	HUB_CORNER_RADIUS_MAX,
	HUB_CORNER_RADIUS_MIN,
	HUB_CORNER_RADIUS_STEP,
	resolveCornerRadiusOverride,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { RangeField } from '@atlassian/company-hub-common/range-field';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import { carouselNewParametersV2 } from '../../../../controllers/use-carousel-extension-parameters-and-actions';
import { type UpdateMainFormFieldType } from '../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import type { CarouselParameters } from '../../../../types';
import { AutoplayToggle } from '../../fields/autoplay-toggle';

import { messages } from './messages';

type CarouselConfigStylesSectionGeneralProps = {
	cornerRadius?: CarouselParameters['cornerRadius'];
	cornerStyle: CarouselParameters['cornerStyle'];
	isAutoplayEnabled: CarouselParameters['isAutoplayEnabled'];
	isCompanyHub: boolean;
	source: string;
	updateMainFormField: UpdateMainFormFieldType;
};

export const CarouselConfigStylesSectionGeneral = ({
	isCompanyHub,
	isAutoplayEnabled,
	updateMainFormField,
	cornerStyle,
	cornerRadius,
	source,
}: CarouselConfigStylesSectionGeneralProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	// Per-element corner radius is Premium, so an un-entitled hub gets no control
	// at all rather than one it could not publish. Scoped to the new flow: the
	// legacy rounded/squared toggle below is not a radius setting and stays.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();
	const theming = useHubTheming();

	const handleCornerStyleChange = useCallback(
		(newCornerStyle: CornerStyle) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'cornerStyling',
					source,
					attributes: {
						cornerStyle: newCornerStyle,
					},
				},
			}).fire();

			updateMainFormField({ cornerStyle: newCornerStyle });
		},
		[createAnalyticsEvent, source, updateMainFormField],
	);

	const handleCornerRadiusChange = useCallback(
		(newCornerRadius: number) => {
			updateMainFormField({ cornerRadius: newCornerRadius });
		},
		[updateMainFormField],
	);

	return (
		<>
			<AutoplayToggle
				isAutoplayEnabled={isAutoplayEnabled}
				updateMainFormField={updateMainFormField}
			/>
			{/* The new-flow slider is Premium-only; the legacy toggle below is not a
			    radius setting, so it is left alone. */}
			{isCompanyHub && isCornerRadiusEnabled && !isCornerRadiusLocked && (
				<RangeField
					label={messages.cornerRadius}
					minValue={HUB_CORNER_RADIUS_MIN}
					maxValue={HUB_CORNER_RADIUS_MAX}
					step={HUB_CORNER_RADIUS_STEP}
					value={resolveHubCornerRadius(
						resolveCornerRadiusOverride(cornerRadius, cornerStyle) ?? theming?.cornerRadius,
					)}
					updateRangeValue={handleCornerRadiusChange}
					analyticSource="carouselExtensionConfigCornerRadius"
					prefix="cornerRadius"
					unit="px"
				/>
			)}
			{isCompanyHub && !isCornerRadiusEnabled && (
				<CornerStyleConfig<CornerStyle>
					cornerStyleValue={cornerStyle || carouselNewParametersV2.cornerStyle}
					updateCornerStyle={handleCornerStyleChange}
					roundCornerValue={CornerStyle.ROUNDED}
					squareCornerValue={CornerStyle.SQUARED}
				/>
			)}
		</>
	);
};
